import {
  Chip,
  formatDate,
  formatMinutes,
  Grid,
  ListContainer,
  ListContext,
  PAGE_DEFAULT,
  PAGE_SIZE_UNLIMITED,
  pn,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  UpdateIconButton,
} from "@compass/core";
import EscalatorWarningIcon from "@mui/icons-material/EscalatorWarning";
import { ListBeProperty } from "components";
import {
  Permission,
  PermissionAction,
  StudentServiceListDto,
  StudentServiceListFilter,
  StudentServiceListResource,
} from "generated/openApi";
import QrCodeIcon from "@mui/icons-material/QrCode";
import { useList, useRouter } from "hooks";
import produce from "immer";
import "moment-timezone";
import { studentServicePageRoute } from "pages/students/student-services/StudentServicePage/StudentServicePage";
import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { isAuthorized } from "services";
import ApiService from "services/apiService";
import { ServiceFilter } from "./ServiceFilter";

export const Services = (props: { initialLoad: boolean; studentId: number; onGetList: () => void }) => {
  const ROOT_PERMISSION: Permission = Permission.StudentService;
  const navigate = useNavigate();
  const { matchTemplatePath } = useRouter();

  const [listFilter, setListFilter] = useState<StudentServiceListFilter>({
    serviceFilter: { studentId: props.studentId },
    page: PAGE_DEFAULT,
    pageSize: PAGE_SIZE_UNLIMITED,
    sortObjects: [{ property: pn<StudentServiceListDto>("date"), isDesc: true }],
  });
  const [listItems, setListItems] = useState<StudentServiceListDto[]>([]);
  const [resource, setResource] = useState<StudentServiceListResource>({});
  const properties = resource.properties || [];

  const { tableContainerRef, loadList, applySort } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.studentServices.getList(listFilter).then((response) => {
        setListItems(response);
      });
    },
  });

  useEffect(() => {
    if (props.initialLoad) {
      loadList(listFilter);

      ApiService.studentServices.getListResource().then((response) => {
        setResource(response);
      });
    }
  }, [props.initialLoad]);

  const header = (
    <Stack direction="row" justifyContent="space-between">
      <Stack direction="row" alignItems="center" spacing="sm" flexGrow={1}>
        <ServiceFilter
          filter={listFilter.serviceFilter || {}}
          setFilter={(filter) => {
            const newListFilter = produce(listFilter, (draft) => {
              draft.serviceFilter = filter;
            });

            setListFilter(newListFilter);
            loadList(newListFilter);
          }}
        />
      </Stack>

      <Stack direction="row" alignItems="center" spacing="sm">
        {/* {isAuthorized(ROOT_PERMISSION, PermissionAction.Create) && (
          <Button
            onClick={() =>
              navigate({
                pathname: studentServicePageRoute.getPath({
                  id: 0,
                  crudMode: "edit",
                  screenType: "dialog",
                }),
                search: studentServicePageRoute.getOptionalParams?.({
                  studentId: props.studentId,
                  hideStudent: true,
                }),
              })
            }
            variant="outlined"
          >
            + New
          </Button>
        )} */}
      </Stack>
    </Stack>
  );

  return (
    <>
      <ListContainer
        fullHeight
        header={header}
        tableContainer={
          <TableContainer ref={tableContainerRef}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <ListBeProperty
                      listProperty={properties.find((x) => x.propertyName === pn<StudentServiceListDto>("date"))}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>
                  <TableCell>
                    <ListBeProperty
                      listProperty={properties.find((x) => x.propertyName === pn<StudentServiceListDto>("serviceType"))}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>
                  <TableCell>
                    <ListBeProperty
                      listProperty={properties.find(
                        (x) => x.propertyName === pn<StudentServiceListDto>("serviceSubType")
                      )}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>
                  {/* <TableCell align="right">
                    <ListBeProperty
                      listProperty={properties.find((x) => x.propertyName === pn<StudentServiceListDto>("parentCount"))}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell> */}
                  <TableCell>
                    <ListBeProperty
                      listProperty={properties.find((x) => x.propertyName === pn<StudentServiceListDto>("serviceName"))}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>
                  <TableCell>Service Location(s)</TableCell>
                  <TableCell>
                    <ListBeProperty
                      listProperty={properties.find(
                        (x) => x.propertyName === pn<StudentServiceListDto>("durationInMinutes")
                      )}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {listItems.map((listItem) => (
                  <TableRow
                    key={listItem.id}
                    onClick={() =>
                      navigate({
                        pathname: studentServicePageRoute.getPath({
                          id: listItem.id,
                          crudMode: "view",
                          screenType: "dialog",
                        }),
                        search: studentServicePageRoute.getOptionalParams?.({
                          studentId: props.studentId,
                          hideStudent: true,
                        }),
                      })
                    }
                  >
                    <TableCell>
                      <Stack direction="row" spacing="sm" alignItems="center">
                        {formatDate(listItem.date)}
                        {listItem.isFromQrCode && (<QrCodeIcon style={{ marginLeft: "10px" }} />)}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing="sm" alignItems="center">
                        <Typography variant="inherit">{listItem.serviceType}</Typography>
                        {listItem?.isForParent && <EscalatorWarningIcon />}
                      </Stack>
                    </TableCell>
                    <TableCell>{listItem.serviceSubType}</TableCell>
                    {/* <TableCell align="right">{listItem.parentCount}</TableCell> */}
                    <TableCell>{listItem.serviceName}</TableCell>
                    <TableCell>
                      {!listItem.isServiceLocationForCollege ? (
                        listItem.school
                      ) : (
                        <Grid container spacing={"sm"}>
                          {listItem.colleges?.map((x) => (
                            <Grid key={x} item>
                              <Chip label={x} />
                            </Grid>
                          ))}
                        </Grid>
                      )}
                    </TableCell>
                    <TableCell>{formatMinutes(listItem.durationInMinutes)}</TableCell>
                    <TableCell type="action">
                      {isAuthorized(ROOT_PERMISSION, PermissionAction.Update) && (
                        <UpdateIconButton
                          onClick={() =>
                            navigate({
                              pathname: studentServicePageRoute.getPath({
                                id: listItem.id,
                                crudMode: "edit",
                                screenType: "dialog",
                              }),
                              search: studentServicePageRoute.getOptionalParams?.({
                                studentId: props.studentId,
                                hideStudent: true,
                              }),
                            })
                          }
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        }
      />

      <ListContext.Provider
        value={{
          loadList: (options) => {
            loadList(listFilter, { resetPage: options?.resetPage });
            props.onGetList();
          },
        }}
      >
        {matchTemplatePath(studentServicePageRoute.templatePath) && <Outlet />}
      </ListContext.Provider>
    </>
  );
};
